import ConditionalWrapper from "../../utils/ConditionalWrapper";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo } from "react";

type RenderTableProps = {
  gridRef?: any;
  gridTheme?: any;
  setGridReady?: (b: boolean) => void;
  columns?: any[];
  rows?: any[];
  rowsPerPage?: number;
  handleChangePage?: (e?: any) => void;
  sideBar?: any;
  label?: string | null;
  resizable?: boolean;
  minimized?: string | null;
  emitEvent?: (action?: string) => void;
  onChange?: (data?: any) => void;

  /** Back-compat: per-row selection event (avoid for 32k rows) */
  onRowSelected?: (row?: any) => void;

  /** Consumer-specific actions (kept for backward compatibility) */
  setSelectedRow?: (row: any) => void;
  setOpenRejectPopup?: (open: boolean) => void;
  isSelectedRow?: boolean;

  /** NEW: Opt-in selection column + scope + perf-friendly event */
  withSelectionColumn?: boolean; // default false (non-breaking)
  selectionScope?: "page" | "all"; // default "page"
  onSelectionChanged?: (e: any) => void;
  selectedCount?: number;

  loading?: boolean;
};

export const RenderTable: React.FC<RenderTableProps> = ({
  gridRef,
  gridTheme,
  setGridReady,
  columns,
  rows,
  rowsPerPage,
  handleChangePage,
  sideBar,
  label,
  resizable,
  minimized,
  emitEvent = () => {},
  onChange,
  loading,

  // legacy actions
  setSelectedRow,
  setOpenRejectPopup,
  isSelectedRow = true,
  onRowSelected,

  // NEW, all optional to keep this component backward-compatible
  withSelectionColumn = false,
  selectionScope = "page",
  onSelectionChanged,
  selectedCount = 0,
}) => {

  /**
   * IMPORTANT:
   * Do NOT inject a manual checkbox column; let AG Grid manage it via rowSelection.
   * This prevents duplicate checkboxes and enables 'currentPage' select-all behavior.
   */
  const finalColumns = useMemo(() => columns ?? [], [columns]);

  /**
   * v31+ rowSelection API:
   * - checkboxes + headerCheckbox create the selection column automatically.
   * - selectAll: 'currentPage' => header checkbox only selects visible page.
   */
  const rowSelection = useMemo(() => {
    if (!isSelectedRow) return undefined;

    if (!withSelectionColumn) {
      // Multi-select via row clicks only (no checkbox column)
      return { mode: "multiRow" as const };
    }

    return {
      mode: "multiRow" as const,
      checkboxes: true,
      headerCheckbox: true,
      selectAll:
        selectionScope === "page" ? ("currentPage" as const) : ("all" as const),
      // If you want selection only via checkboxes (no row click), uncomment:
      // enableClickSelection: false,
    };
  }, [isSelectedRow, withSelectionColumn, selectionScope]);

  const overLay = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} sx={{ color: "#0a0101" }} />
      </Box>
    );
  };
  // Memoized so AG Grid always receives the same object reference.
  // Passing a new inline object on every render causes AG Grid to detect that
  // filterValueGetter changed and clear the active filter model, making all
  // rows reappear after checkbox clicks.
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: { buttons: ["reset", "apply"], closeOnApply: true },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: { buttons: ["reset"] },
          },
        ],
      },
      resizable: true,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "18px",
      },
      valueFormatter: (p: any) => {
        const isSelectionCol =
          p?.column?.getColId?.() === "ag-Grid-AutoColumn" ||
          !p?.colDef?.field;
        if (isSelectionCol) return "";
        const v = p?.value;
        if (
          v === null ||
          v === undefined ||
          (typeof v === "string" && v.trim() === "") ||
          (typeof v === "number" && Number.isNaN(v))
        ) {
          return "-";
        }
        return v;
      },
      filterValueGetter: (params: any) => {
        const v = params.data?.[params.colDef?.field];
        if (v === null || v === undefined || v === "-" || v === "") return null;
        const str = String(v);

        // Preserve literal text values (e.g. "0123") for text columns.
        // Only coerce to number when the column actually has a number filter.
        const colFilters = params.colDef?.filterParams?.filters;
        const hasNumberFilter =
          params.colDef?.filter === "agNumberColumnFilter" ||
          (Array.isArray(colFilters) &&
            colFilters.some((f: any) => f?.filter === "agNumberColumnFilter"));
        if (!hasNumberFilter) return str;

        const noCommas = str.replace(/,/g, "");
        const asNum = Number(noCommas.replace(/[$€£¥%]/g, "").trim());
        // Return actual number so agNumberColumnFilter can do range comparisons.
        // Text columns (where asNum is NaN) fall back to the original string.
        return Number.isFinite(asNum) ? asNum : str;
      },
      comparator: (a: any, b: any) => {
        const toNum = (v: any) => {
          if (v === null || v === undefined || v === "-" || v === "") return null;
          const n = Number(String(v).replace(/,/g, "").replace(/[$€£¥%]/g, "").trim());
          return Number.isFinite(n) ? n : null;
        };
        const na = toNum(a);
        const nb = toNum(b);
        if (na !== null && nb !== null) return na - nb;
        return String(a ?? "").localeCompare(String(b ?? ""));
      },
    }),
    [],
  );
  /**
   * Clear selection when the page changes
   * (matches “once page change selection should go out”)
   */
  const handlePaginationChanged = (e: any) => {
    // If consumer provides custom pagination handling (e.g., warning dialog flow),
    // delegate fully and avoid automatic clear here.
    if (handleChangePage) {
      handleChangePage(e);
      return;
    }

    // newPage is true only when the page number actually changed
    if (e?.newPage) {
      e.api?.deselectAll?.(); // clear selections on page change
    }
  };

  const handleSelectionHeaderClick = useCallback((e: any) => {
    const colId = e?.column?.getColId?.();
    const isSelectionHeader =
      colId === "ag-Grid-SelectionColumn" || colId === "__select__";
    if (!isSelectionHeader) return;

    const api = e?.api;
    if (!api) return;

    // Let AG Grid finish header checkbox toggle first.
    setTimeout(() => {
      const currentPage = api.paginationGetCurrentPage?.() ?? 0;
      const pageSize = api.paginationGetPageSize?.() ?? 0;
      const displayedCount = api.getDisplayedRowCount?.() ?? 0;
      const start = currentPage * pageSize;
      const end = Math.min(start + pageSize, displayedCount);

      let selectedOnPage = 0;
      for (let i = start; i < end; i += 1) {
        const node = api.getDisplayedRowAtIndex?.(i);
        if (node?.isSelected?.()) selectedOnPage += 1;
      }

      const totalSelected = api.getSelectedNodes?.().length ?? 0;
      if (totalSelected > 0 && selectedOnPage === 0) {
        api.deselectAll?.();
      }
    }, 0);
  }, []);

  return (
    <ConditionalWrapper
      condition={minimized !== label}
      elseContent={
        <Box paddingLeft={1}>
          <Typography>
            {label} is minimized, Click here to restore
            <IconButton onClick={() => emitEvent("")}>
              <ArrowOutwardIcon color="primary" />
            </IconButton>
          </Typography>
        </Box>
      }
    >
      <Stack
        direction="row"
        sx={{
          minHeight: resizable ? "180px" : null,
          height: resizable ? "250px" : null,
          resize: resizable ? "vertical" : null,
          overflow: resizable ? "auto" : null,
          maxHeight:
            resizable && minimized !== "" && minimized !== label
              ? null
              : resizable
                ? "325px"
                : null,
          flexGrow: resizable
            ? null
            : minimized !== "" && minimized !== label
              ? 4
              : 1,
          flexShrink: minimized === label ? 1 : 0,
        }}
      >
        <ConditionalWrapper condition={!!label}>
          <Stack
            gap={2}
            style={{
              fontSize: "16px",
              fontWeight: 500,
              transform: "rotate(0deg)",
              backgroundColor: "#fff",
              flex: 0,
              flexDirection: "column-reverse",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: 2,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                writingMode: "sideways-lr",
                textOrientation: "upright",
                textAlign: "center",
                display: "inline-block",
                fontSize: "16px",
                fontWeight: 500,
                padding: "4px 8px",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Stack>
        </ConditionalWrapper>

        <Box sx={{ flex: 1, position: "relative" }}>
          <AgGridReact
            // ref & ready
            ref={gridRef}
            loading={loading}
            onGridReady={() => setGridReady?.(true)}
            theme={gridTheme}
            loadingOverlayComponent={overLay}
            overlayNoRowsTemplate={"<span>No Data Available</span>"}
            //  stable row identity
            getRowId={(params) => {
              const rowId =
                params.data.__uid ??
                params?.data?.materialNumber ??
                params?.data?.id ??
                params?.data?.rowIndex;
              return String(rowId);
            }}
            // columns
            columnDefs={finalColumns}
            //  Page-only header "Select All" when withSelectionColumn is true
            {...(isSelectedRow ? { rowSelection } : {})}
            // paging / model
            enableCharts={true}
            pagination={true}
            paginationPageSize={rowsPerPage}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            rowModelType={"clientSide"}
            rowData={rows}
            onPaginationChanged={handlePaginationChanged}
            onColumnHeaderClicked={handleSelectionHeaderClick}
            gridOptions={{ sideBar }}
            // selection events
            onSelectionChanged={onSelectionChanged}
            onRowSelected={onRowSelected}
            // editing / UX
            stopEditingWhenCellsLoseFocus={true}
            onCellValueChanged={onChange}
            onCellEditRequest={(e) => console.log(e.data)}
            singleClickEdit
            suppressMenuHide={true}
            enableCellTextSelection={true}
            suppressRowTransform={true}
            // default col def — memoized so AG Grid never sees a new reference on
            // re-render (a new filterValueGetter reference causes AG Grid to clear
            // the active filter model, making all rows reappear after checkbox clicks)
            defaultColDef={defaultColDef}
            // context menu (kept for compatibility)
            getContextMenuItems={(params) => {
              const defaultItems = params.defaultItems ?? [];
              return [
                ...defaultItems,
                {
                  name: "Review Details",
                  action: () => {
                    if (params.node && params.node.data) {
                      setSelectedRow?.(params.node.data);
                      setOpenRejectPopup?.(true);
                    } else {
                      alert("No row selected");
                    }
                  },
                  disabled: !params.node?.data,
                },
              ];
            }}
          />
          {selectedCount > 0 && (
            <Typography
              sx={{
                position: "absolute",
                left: 12,
                bottom: 8,
                zIndex: 2,
                fontSize: 13,
                fontWeight: 500,
                color: "text.secondary",
                pointerEvents: "none",
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {selectedCount}
              </Box>{" "}
              item{selectedCount !== 1 ? "s" : ""} selected
            </Typography>
          )}
        </Box>
      </Stack>
    </ConditionalWrapper>
  );
};


